import React, { useRef, useState, useEffect, useCallback } from 'react';
import './Solutions.css';
import { Brain, RefreshCw, Zap, Factory, Workflow, ClipboardList, Cloud, Building2, ArrowUpRight, ArrowRight } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import Button from '../components/Button';

/* ────────────────────────────────────────────────────
   Hook: prefers-reduced-motion
   ──────────────────────────────────────────────────── */
const usePrefersReducedMotion = () => {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReduced(mq.matches);
    const handler = (e: MediaQueryListEvent) => setReduced(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);
  return reduced;
};

/* ────────────────────────────────────────────────────
   CardBorder — animatable SVG chamfered rectangle
   ──────────────────────────────────────────────────── */
const CardBorder: React.FC<{
  width: number;
  height: number;
  isActive: boolean;
}> = ({ width, height, isActive }) => {
  const c = 16; // chamfer diagonal
  const m = 1;  // inset to prevent stroke clipping
  if (width === 0 || height === 0) return null;

  const w = width;
  const h = height;
  const pathData = [
    `M ${c + m} ${m}`,
    `L ${w - c - m} ${m}`,
    `L ${w - m} ${c + m}`,
    `L ${w - m} ${h - c - m}`,
    `L ${w - c - m} ${h - m}`,
    `L ${c + m} ${h - m}`,
    `L ${m} ${h - c - m}`,
    `L ${m} ${c + m}`,
    'Z',
  ].join(' ');

  const iw = w - 2 * m;
  const ih = h - 2 * m;
  const chamferLen = Math.sqrt(2 * c * c);
  const perimeter = 2 * (iw - 2 * c) + 2 * (ih - 2 * c) + 4 * chamferLen;

  return (
    <svg className="card-border-svg" width={w} height={h} aria-hidden="true">
      {/* Idle border */}
      <path d={pathData} className="card-border-bg" fill="transparent" />
      {/* Accent border — stroke-dashoffset drives the "drawing" animation */}
      <path
        d={pathData}
        className="card-border-accent"
        fill="transparent"
        strokeDasharray={perimeter}
        style={{
          strokeDashoffset: isActive ? 0 : perimeter,
          transition: isActive
            ? 'stroke-dashoffset 0.6s cubic-bezier(0.4,0,0.2,1)'
            : 'stroke-dashoffset 0.4s cubic-bezier(0.4,0,0.2,1) 0.15s',
        }}
      />
    </svg>
  );
};


/* ────────────────────────────────────────────────────
   CircuitTraces — draws connection lines on background
   ──────────────────────────────────────────────────── */
interface TraceCoord {
  x1: number; y1: number;
  x2: number; y2: number;
  type: 'horizontal' | 'vertical';
}

const CircuitTraces: React.FC<{
  containerEl: HTMLDivElement | null;
  cardEls: (HTMLDivElement | null)[];
  bootIndex: number;
  hoveredIdx: number | null;
}> = ({ containerEl, cardEls, bootIndex, hoveredIdx }) => {
  const [coords, setCoords] = useState<TraceCoord[]>([]);

  useEffect(() => {
    if (!containerEl) return;

    const ro = new ResizeObserver(() => {
      // ResizeObserver callbacks run after layout.
      // Reading getBoundingClientRect here does not force a new layout.
      const cr = containerEl.getBoundingClientRect();
      const rects = cardEls.map(el => el ? el.getBoundingClientRect() : null);

      const next: TraceCoord[] = [];
      for (let i = 0; i < rects.length - 1; i++) {
        const ra = rects[i];
        const rb = rects[i + 1];
        if (!ra || !rb) continue;

        const r1 = {
          l: ra.left - cr.left, r: ra.right - cr.left,
          t: ra.top - cr.top, b: ra.bottom - cr.top,
          w: ra.width, h: ra.height,
        };
        const r2 = {
          l: rb.left - cr.left, r: rb.right - cr.left,
          t: rb.top - cr.top, b: rb.bottom - cr.top,
          w: rb.width, h: rb.height,
        };

        const sameRow = Math.abs(r1.t - r2.t) < 40;
        if (sameRow) {
          next.push({
            x1: r1.r, y1: r1.t + r1.h / 2,
            x2: r2.l, y2: r2.t + r2.h / 2,
            type: 'horizontal',
          });
        } else {
          next.push({
            x1: r1.l + r1.w / 2, y1: r1.b,
            x2: r2.l + r2.w / 2, y2: r2.t,
            type: 'vertical',
          });
        }
      }
      setCoords(next);
    });

    ro.observe(containerEl);
    return () => ro.disconnect();
  }, [containerEl, cardEls]);

  if (!coords.length) return null;

  return (
    <svg className="circuit-traces-svg" aria-hidden="true">
      {coords.map((c, idx) => {
        let d = '';
        if (c.type === 'horizontal') {
          const mx = (c.x1 + c.x2) / 2;
          const dy = c.y2 - c.y1;
          if (Math.abs(dy) < 5) {
            d = `M ${c.x1} ${c.y1} L ${c.x2} ${c.y2}`;
          } else {
            d = `M ${c.x1} ${c.y1} L ${mx - 8} ${c.y1} L ${mx + 8} ${c.y2} L ${c.x2} ${c.y2}`;
          }
        } else {
          const my = (c.y1 + c.y2) / 2;
          const dx = c.x2 - c.x1;
          if (Math.abs(dx) < 10) {
            d = `M ${c.x1} ${c.y1} L ${c.x2} ${c.y2}`;
          } else {
            const s = dx > 0 ? 1 : -1;
            d = `M ${c.x1} ${c.y1} L ${c.x1} ${my - 12} L ${c.x1 + s * 12} ${my} L ${c.x2 - s * 12} ${my} L ${c.x2} ${my + 12} L ${c.x2} ${c.y2}`;
          }
        }

        const isTraceActive =
          bootIndex === 2 * idx + 1 ||
          hoveredIdx === idx ||
          hoveredIdx === idx + 1;

        return (
          <g key={idx}>
            <path d={d} className="circuit-trace-idle" fill="none" />
            <path
              key={`p-${idx}-${isTraceActive}`}
              d={d}
              pathLength="100"
              className={`circuit-trace-active${isTraceActive ? ' pulse-anim' : ''}`}
              fill="none"
            />
          </g>
        );
      })}
    </svg>
  );
};

/* ────────────────────────────────────────────────────
   SolutionCard — child component
   ──────────────────────────────────────────────────── */
interface SolutionCardProps {
  idx: number;
  title: string;
  desc: string;
  icon: React.ReactNode;
  isBooting: boolean;
  onSelect: () => void;
  onHoverChange: (hovered: boolean) => void;
  cardElsRef: React.MutableRefObject<(HTMLDivElement | null)[]>;
}

const SolutionCard: React.FC<SolutionCardProps> = ({
  idx,
  title,
  desc,
  icon: _icon,
  isBooting,
  onSelect,
  onHoverChange,
  cardElsRef,
}) => {
  const outerRef = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState({ width: 0, height: 0 });
  const [isHovered, setIsHovered] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const el = outerRef.current;
    if (!el) return;
    cardElsRef.current[idx] = el;

    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        if (entry.target === el) {
          // Use borderBoxSize if available (matches offsetWidth/Height), fallback to contentRect
          const box = entry.borderBoxSize ? entry.borderBoxSize[0] : null;
          const width = box ? box.inlineSize : entry.contentRect.width;
          const height = box ? box.blockSize : entry.contentRect.height;
          setSize({ width, height });
        }
      }
    });
    ro.observe(el);

    const io = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsVisible(true);
        io.disconnect();
      }
    }, { threshold: 0.1 });
    io.observe(el);

    return () => {
      ro.disconnect();
      io.disconnect();
      // eslint-disable-next-line react-hooks/exhaustive-deps
      cardElsRef.current[idx] = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idx]);

  const isActive = isHovered || isBooting;

  return (
    <div
      ref={outerRef}
      className={`solution-card-outer${isHovered ? ' hovered' : ''}${isBooting ? ' booting' : ''}`}
      onClick={onSelect}
      onMouseEnter={() => { setIsHovered(true); onHoverChange(true); }}
      onMouseLeave={() => { setIsHovered(false); onHoverChange(false); }}
    >
      <CardBorder width={size.width} height={size.height} isActive={isActive} />

      <div
        className={`solution-card ${isVisible ? 'active' : ''}`}
        style={{ transitionDelay: `${idx * 0.07}s` }}
      >
        <div className="solution-card-content">
          <div className="solution-header-row" style={{ justifyContent: 'flex-end' }}>
            <button className="solution-arrow-btn" aria-label={`View details about ${title}`}>
              <ArrowUpRight size={18} />
            </button>
          </div>
          <h3>{title}</h3>
          <p>{desc}</p>
        </div>
      </div>
    </div>
  );
};

/* ────────────────────────────────────────────────────
   Solutions — main section component
   ──────────────────────────────────────────────────── */
interface SolutionsProps {
  onSelectSolution?: (title: string) => void;
  preview?: boolean;
}

export const Solutions: React.FC<SolutionsProps> = ({ onSelectSolution, preview = false }) => {
  const navigate = useNavigate();
  const containerRef = useRef<HTMLDivElement>(null);
  const cardElsRef = useRef<(HTMLDivElement | null)[]>([]);
  const [containerEl, setContainerEl] = useState<HTMLDivElement | null>(null);
  const [hoveredIdx, setHoveredIdx] = useState<number | null>(null);
  const [bootIndex, setBootIndex] = useState(-1);
  const isReducedMotion = usePrefersReducedMotion();

  const containerCallbackRef = useCallback((node: HTMLDivElement | null) => {
    containerRef.current = node;
    setContainerEl(node);
  }, []);

  const solutionsList = [
    {
      id: 'ai-business-solutions',
      title: 'AI Business Solutions',
      desc: 'Helps businesses unlock the power of Artificial Intelligence to work smarter, improve efficiency, and make better decisions.',
      icon: <Brain size={24} />,
      eyebrow: 'COGNITIVE_CORE',
    },
    {
      id: 'enterprise-digital-transformation',
      title: 'Digital Transformation',
      desc: 'Accelerate business growth with digital transformation solutions that streamline operations, enhance customer experiences, improve productivity, and drive innovation.',
      icon: <RefreshCw size={24} />,
      eyebrow: 'TRANSFORM_STACK',
    },
    {
      id: 'intelligent-automation-systems',
      title: 'Intelligent Automation',
      desc: 'Combine Artificial Intelligence, machine learning, and automation technologies, enable organizations to reduce manual effort, improve accuracy.',
      icon: <Zap size={24} />,
      eyebrow: 'AUTOMATION_NET',
    },
    {
      id: 'industrial-iot-solutions',
      title: 'Industrial IoT Solutions',
      desc: 'Connect machines, monitor operations in real time, and optimize industrial performance with intelligent IoT solutions.',
      icon: <Factory size={24} />,
      eyebrow: 'TELEMETRY_EDGE',
    },
    {
      id: 'enterprise-workflow-platforms',
      title: 'Workflow Patterns',
      desc: 'Help organizations create seamless workflows that reduce delays, minimize errors, and increase productivity.',
      icon: <Workflow size={24} />,
      eyebrow: 'INTEGRATED_FLOW',
    },
    {
      id: 'business-process-automation',
      title: 'Business process Automation',
      desc: 'Our automation solutions simplify everyday business processes such as data management, reporting, and customer interactions.',
      icon: <ClipboardList size={24} />,
      eyebrow: 'PROCESS_ENGINE',
    },
    {
      id: 'cloud-based-systems',
      title: 'Cloud-Based Systems',
      desc: 'Empower your business with cloud-based systems that enhance security, streamline operations, and enable seamless collaboration from anywhere.',
      icon: <Cloud size={24} />,
      eyebrow: 'INFRASTRUCTURE_CLOUD',
    },
    {
      id: 'scalable-enterprise-applications',
      title: 'Enterprise Application',
      desc: 'Develop ERP systems, CRM platforms, workflow management solutions, and custom business applications tailored to your business needs.',
      icon: <Building2 size={24} />,
      eyebrow: 'ENTERPRISE_PORTAL',
    },
  ];

  // If in preview mode, only show the first 4 solutions
  const displayList = preview ? solutionsList.slice(0, 4) : solutionsList;

  useEffect(() => {
    if (isReducedMotion) { setBootIndex(-1); return; }
    let step = 0;
    const id = setInterval(() => {
      setBootIndex(step);
      step++;
      if (step > 18) { step = 0; }
    }, 350);
    return () => clearInterval(id);
  }, [isReducedMotion]);

  return (
    <section className="section solutions-section" id="solutions">
      <div className="container">
        <div className="section-header centered">
          {/* Eyebrow removed as requested */}
          <h2 className="section-title">Solutions That Solve Real Business Challenges</h2>
          <p>At Vivify, we deliver customized technology solutions that empower organizations to overcome challenges, optimize operations, and drive sustainable growth.</p>
        </div>

        <div className="solutions-container-wrapper" ref={containerCallbackRef}>
          {!isReducedMotion && !preview && (
            <CircuitTraces
              containerEl={containerEl}
              cardEls={cardElsRef.current}
              bootIndex={bootIndex}
              hoveredIdx={hoveredIdx}
            />
          )}

          <div className="solutions-container grid grid-4">
            {displayList.map((sol, idx) => (
              <SolutionCard
                key={idx}
                idx={idx}
                title={sol.title}
                desc={sol.desc}
                icon={sol.icon}
                isBooting={bootIndex === 2 * idx}
                onSelect={() => {
                  if (sol.id) {
                    navigate(`/solutions/${sol.id}`);
                  } else {
                    onSelectSolution?.(sol.title);
                  }
                }}
                onHoverChange={(h) => setHoveredIdx(h ? idx : null)}
                cardElsRef={cardElsRef}
              />
            ))}
          </div>
        </div>

        {preview && (
          <div className="section-footer centered" style={{ marginTop: '3rem' }}>
            <Link to="/solutions">
              <Button variant="primary" size="md" icon={<ArrowRight size={16} />}>
                View All Solutions
              </Button>
            </Link>
          </div>
        )}
      </div>
    </section>
  );
};

export default Solutions;
